import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, Shield, CheckCircle, Eye, EyeOff, QrCode, Download, LogOut, AlertTriangle, ChevronRight, Smartphone } from 'lucide-react';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  avatar: string;
}

interface CustomerProject {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'maintenance';
  date: string;
  value: string;
}

interface CustomerWarranty {
  id: string;
  product: string;
  expires: string;
  status: 'active' | 'expiring' | 'expired';
  qrCode: string;
}

interface CustomerInvoice {
  id: string;
  amount: string;
  date: string;
  status: 'paid' | 'pending' | 'overdue';
}

const mockCustomer: Customer = {
  id: 'CUS-001',
  name: 'Rajesh Gunawardena',
  email: 'rajesh@aiainsurance.lk',
  phone: '+94711234567',
  company: 'AIA Insurance PLC',
  avatar: 'RG',
};

const projects: CustomerProject[] = [
  { id: 'PRJ-001', name: 'CCTV Installation — Colombo 3 Office', status: 'active', date: '2026-07-10', value: 'LKR 485,000' },
  { id: 'PRJ-002', name: 'Alarm System — Maradana Branch', status: 'completed', date: '2026-03-15', value: 'LKR 185,000' },
  { id: 'PRJ-003', name: 'Annual Maintenance Contract', status: 'maintenance', date: '2026-01-01', value: 'LKR 42,000/yr' },
];

const warranties: CustomerWarranty[] = [
  { id: 'WR-001', product: 'Hikvision CCTV System (12 cameras)', expires: '2028-07-10', status: 'active', qrCode: 'WR-001-QR' },
  { id: 'WR-002', product: 'Paradox Alarm System', expires: '2027-03-15', status: 'active', qrCode: 'WR-002-QR' },
  { id: 'WR-003', product: 'Dahua NVR DS-96128NI', expires: '2026-08-10', status: 'expiring', qrCode: 'WR-003-QR' },
];

const invoices: CustomerInvoice[] = [
  { id: 'INV-047', amount: 'LKR 485,000', date: 'Jul 8, 2026', status: 'paid' },
  { id: 'INV-051', amount: 'LKR 85,000', date: 'Jul 12, 2026', status: 'pending' },
  { id: 'INV-021', amount: 'LKR 185,000', date: 'Mar 18, 2026', status: 'paid' },
];

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-600',
  maintenance: 'bg-blue-100 text-blue-700',
  expiring: 'bg-amber-100 text-amber-700',
  expired: 'bg-red-100 text-red-700',
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  overdue: 'bg-red-100 text-red-700',
};

type Section = 'overview' | 'projects' | 'warranty' | 'invoices' | 'support';

export default function CustomerPortalPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [email, setEmail] = useState('rajesh@aiainsurance.lk');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>('overview');
  const [loginError, setLoginError] = useState('');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password.length >= 4) {
      setLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Use any email + password (4+ chars) for demo');
    }
  };

  const navItems: { id: Section; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'projects', label: 'Projects' },
    { id: 'warranty', label: 'Warranty' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'support', label: 'Support' },
  ];

  if (!loggedIn) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] flex items-center justify-center shadow-xl shadow-[rgba(127,168,106,0.2)] mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Portal</h1>
            <p className="text-sm text-gray-500 mt-1">K-Tec Security Solutions</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8">
            <h2 className="text-base font-semibold text-gray-900 mb-6">Sign in to your account</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">Email Address</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D8E7D1] focus:border-[#7FA86A]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full pl-10 pr-10 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D8E7D1] focus:border-[#7FA86A]"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              {loginError && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {loginError}
                </div>
              )}
              <button type="submit" className="w-full bg-gradient-to-r from-[#7FA86A] to-[#96BE82] text-white font-medium text-sm py-3 rounded-xl hover:shadow-lg hover:shadow-[rgba(127,168,106,0.2)] transition-all mt-2">
                Sign In
              </button>
            </form>
            <div className="mt-4 flex items-center gap-2 justify-center">
              <Smartphone className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">Also available on iOS & Android</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#7FA86A] to-[#96BE82] px-6 pt-6 pb-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center text-sm font-bold text-white">
              {mockCustomer.avatar}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">{mockCustomer.name}</h2>
              <p className="text-xs text-white/80">{mockCustomer.company}</p>
            </div>
          </div>
          <button onClick={() => setLoggedIn(false)} className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          {[['Active Projects', '1'], ['Warranties', '3'], ['Invoices', '3']].map(([label, val]) => (
            <div key={label as string} className="bg-white/20 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-white">{val}</p>
              <p className="text-[11px] text-white/80">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Nav */}
      <div className="px-6 -mt-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex overflow-x-auto">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`flex-1 py-3 text-xs font-medium whitespace-nowrap transition-colors ${activeSection === item.id ? 'text-[#658C53] border-b-2 border-[#7FA86A]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <AnimatePresence mode="wait">
          <motion.div key={activeSection} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {activeSection === 'overview' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Account Details</h3>
                  <div className="space-y-2">
                    {[['Email', mockCustomer.email], ['Phone', mockCustomer.phone], ['Customer ID', mockCustomer.id], ['Company', mockCustomer.company || '—']].map(([label, val]) => (
                      <div key={label as string} className="flex justify-between text-xs">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-medium text-gray-900">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Download All Invoices', icon: Download, action: () => {} },
                    { label: 'Service Request', icon: Smartphone, action: () => setActiveSection('support') },
                  ].map((btn, i) => {
                    const Icon = btn.icon;
                    return (
                      <button key={i} onClick={btn.action} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-50 transition-colors">
                        <span className="text-xs font-medium text-gray-700">{btn.label}</span>
                        <Icon className="w-4 h-4 text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {activeSection === 'projects' && (
              <div className="space-y-3">
                {projects.map(p => (
                  <div key={p.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-gray-400">{p.id}</p>
                        <p className="text-sm font-semibold text-gray-900 mt-0.5">{p.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{p.date} · {p.value}</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[p.status]}`}>{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'warranty' && (
              <div className="space-y-3">
                {warranties.map(w => (
                  <div key={w.id} className={`bg-white rounded-2xl border p-4 ${w.status === 'expiring' ? 'border-amber-200' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-gray-400">{w.id}</p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[w.status]}`}>{w.status}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-0.5">{w.product}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Expires: {w.expires}</p>
                      </div>
                      <button className="flex items-center gap-1 text-xs text-[#658C53] border border-[#D8E7D1] bg-[#F1F6EE] rounded-lg px-2 py-1">
                        <QrCode className="w-3 h-3" /> QR
                      </button>
                    </div>
                    {w.status === 'expiring' && (
                      <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3" /> Warranty expiring soon. Contact us to renew.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'invoices' && (
              <div className="space-y-3">
                {invoices.map(inv => (
                  <div key={inv.id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${statusColors[inv.status]}`}>
                      <Download className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{inv.id}</p>
                      <p className="text-xs text-gray-500">{inv.amount} · {inv.date}</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[inv.status]}`}>{inv.status}</span>
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'support' && (
              <div className="space-y-3">
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Submit Service Request</h3>
                  <div className="space-y-3">
                    <select className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#D8E7D1]">
                      <option>Select service type…</option>
                      <option>CCTV Not Working</option>
                      <option>Alarm False Trigger</option>
                      <option>Network Issue</option>
                      <option>Annual Maintenance</option>
                    </select>
                    <textarea rows={3} placeholder="Describe the issue…" className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#D8E7D1] resize-none" />
                    <button className="w-full bg-gradient-to-r from-[#7FA86A] to-[#96BE82] text-white text-sm font-medium py-2.5 rounded-xl hover:shadow-[rgba(127,168,106,0.2)] hover:shadow-lg transition-all">
                      Submit Request
                    </button>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-900">WhatsApp Support</p>
                    <p className="text-xs text-gray-500">Chat with us on +94 11 234 5678</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
